/**
 * ID generator abstraction for generating unique identifiers.
 * Enables deterministic testing by allowing ID generation to be controlled in tests.
 */

/**
 * Interface for ID generation.
 */
export interface IdGenerator {
    /**
     * Generates a unique identifier string.
     */
    generate(): string
}

/**
 * Random ID generator using Math.random().
 * Use this in production code for generating unique IDs.
 */
export const randomIdGenerator: IdGenerator = {
    generate: () => Math.random().toString(36).substring(2, 11),
}

/**
 * Factory function to create a sequential ID generator.
 * Use this in tests for predictable, deterministic IDs.
 *
 * @param prefix - Optional prefix for generated IDs (defaults to 'id')
 * @returns An IdGenerator that produces sequential IDs like "id-1", "id-2", etc.
 */
export const createSequentialIdGenerator = (
    prefix = 'id'
): IdGenerator & {
    reset: () => void
    getCounter: () => number
} => {
    let counter = 0

    return {
        generate: () => `${prefix}-${++counter}`,
        reset: () => {
            counter = 0
        },
        getCounter: () => counter,
    }
}

/**
 * Factory function to create an ID generator from a predefined list.
 * Use this in tests when you need specific ID values.
 *
 * @param ids - Array of IDs to return in order
 * @returns An IdGenerator that returns IDs from the list
 * @throws Error if generate() is called more times than there are IDs
 */
export const createFixedIdGenerator = (
    ids: string[]
): IdGenerator & {
    remaining: () => number
} => {
    let index = 0
    const idList = [...ids]

    return {
        generate: () => {
            if (index >= idList.length) {
                throw new Error('FixedIdGenerator: No more IDs available')
            }
            return idList[index++]
        },
        remaining: () => idList.length - index,
    }
}
